import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserRole, ApiResponse, LoginInput } from '@ouagaliv/shared';
import { api } from '../lib/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginInput) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          await refreshUser();
        } catch {
          logout();
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const refreshUser = async () => {
    const res = await api.get<ApiResponse<{ user: User }>>('/auth/profile');
    if (res.data.success && res.data.data) {
      if (res.data.data.user.role !== UserRole.ADMIN) {
        throw new Error('Accès réservé aux administrateurs');
      }
      setUser(res.data.data.user);
    }
  };

  const login = async (data: LoginInput) => {
    const res = await api.post<ApiResponse<{ user: User; tokens: { accessToken: string; refreshToken: string } }>>('/auth/login', data);
    if (!res.data.success) throw new Error(res.data.error?.message || 'Email ou mot de passe incorrect');
    if (!res.data.data) throw new Error('Email ou mot de passe incorrect');
    if (res.data.data.user.role !== UserRole.ADMIN) throw new Error('Accès réservé aux administrateurs');
    localStorage.setItem('accessToken', res.data.data.tokens.accessToken);
    localStorage.setItem('refreshToken', res.data.data.tokens.refreshToken);
    setUser(res.data.data.user);
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated: !!user, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
